import type { TourStep } from "../TourProvider";

/**
 * Whether a step can be spotlighted right now.
 * Steps without a target function are kept (Ant Design centers them).
 */
export const isTourTargetAvailable = (step: TourStep): boolean => {
  if (typeof step.target !== "function") {
    return true;
  }

  try {
    const el = step.target();
    // Truthy DOM node is enough — avoid brittle HTMLElement instanceof checks in tests
    return el != null;
  } catch {
    return false;
  }
};

/**
 * Drop steps whose DOM targets are missing (gated UI, unauth buttons, etc.).
 * Hoists tabNavigation / recommendedTours from the original last step onto the
 * new last step when the original last step was filtered out.
 */
export const resolveTourSteps = (steps: TourStep[]): TourStep[] => {
  if (steps.length === 0) {
    return [];
  }

  const available = steps.filter(isTourTargetAvailable);
  if (available.length === 0) {
    return [];
  }

  const originalLast = steps[steps.length - 1];
  const lastIndex = available.length - 1;
  const newLast = available[lastIndex];

  if (
    originalLast &&
    newLast &&
    originalLast !== newLast &&
    (originalLast.tabNavigation || originalLast.recommendedTours)
  ) {
    available[lastIndex] = {
      ...newLast,
      tabNavigation: newLast.tabNavigation ?? originalLast.tabNavigation,
      recommendedTours: newLast.recommendedTours ?? originalLast.recommendedTours,
    };
  }

  return available;
};
